import { formatUsdMicros, parseChainAmount } from '../../core/pricing'
import type { UnitPricesDto } from '../../data/types'
import { CAPABILITY_ROWS } from './CatalogCapabilities'

/**
 * The provider's own rate card, one row per resource.
 *
 * The rows come from `CAPABILITY_ROWS`, the same list the Capabilities and
 * Available now columns map over, so the icons, order and row heights line up
 * across all three columns by construction rather than by three hand-written
 * lists agreeing for as long as nobody edits one of them.
 *
 * What this replaced was a single figure per host: the bidengine's indicative
 * bid for the fixed `medium-v1` shape, which is why every row showed the same
 * number. It answered a different question from the price a tenant pays on the
 * host-rental path "Rent this host" opens, and the two have differed by ~12x.
 */

/** The unit each row's price is quoted per, shown beside the figure. */
const UNIT_LABELS: Record<(typeof CAPABILITY_ROWS)[number]['id'], string> = {
  cpu: 'per core',
  memory: 'per GB',
  storage: 'per GB',
  gpu: 'per GPU',
}

/**
 * Each amount is the provider's own list price, uddt per unit per HOUR (per
 * core for CPU) — exactly what the operator wrote, so the figure needs no
 * conversion beyond uddt = microdollars. An amount this console cannot read
 * exactly is null, and the row shows the dash.
 */
function hourlyText(id: (typeof CAPABILITY_ROWS)[number]['id'], prices: UnitPricesDto): string | null {
  const micros = parseChainAmount(prices.denom === 'uddt' ? prices[id] : null)
  return micros === null ? null : `${formatUsdMicros(micros)}/hour`
}

export function CatalogUnitPrices({ prices }: { prices: UnitPricesDto }) {
  return <div className="catalog-unit-prices" aria-label="Price $/hour">
    {CAPABILITY_ROWS.map(({ id, label, Icon }) => {
      const unit = UNIT_LABELS[id]
      const text = hourlyText(id, prices)
      return <div key={id} className="catalog-unit-price-row" role="group" aria-label={`${label} ${unit}`}>
        <Icon className="catalog-capability-icon" size={20} strokeWidth={1.8} aria-hidden="true" />
        <strong className="catalog-unit-price-amount" title={text === null ? `${label} price not reported` : `${label} ${unit}`}>
          {/*
            A row with no readable figure renders the muted em-dash the sibling
            columns already use for an unreported value — never `$0.00`, which
            would state that the provider gives this resource away.
          */}
          {text === null ? <span className="muted">—</span> : <span className="money">{text}</span>}
        </strong>
        <span className="catalog-unit-price-unit">{unit}</span>
      </div>
    })}
  </div>
}
